/**
 * Workflow Trigger and Action Definitions
 * This file contains the library of all available triggers and actions
 */
// ============================================================================
// TRIGGER DEFINITIONS
// ============================================================================
export const TRIGGER_DEFINITIONS = {
    when_item_created: {
        type: 'when_item_created',
        label: 'When item created',
        description: 'Triggers when a new item is created',
        icon: 'Plus',
        category: 'data',
        configurable: true,
        fields: [
            {
                id: 'source',
                name: 'source',
                label: 'Item type',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Task/Step', value: 'steps' },
                    { label: 'Mission', value: 'missions' },
                    { label: 'Length', value: 'lengths' },
                    { label: 'Calendar event', value: 'calendar_events' },
                ],
            },
        ],
    },
    when_status_changes: {
        type: 'when_status_changes',
        label: 'When status changes to something',
        description: 'Triggers when an item status changes',
        icon: 'Settings',
        category: 'data',
        configurable: true,
        fields: [
            {
                id: 'source',
                name: 'source',
                label: 'Item type',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Mission', value: 'missions' },
                    { label: 'Project task', value: 'project_tasks' },
                ],
            },
            {
                id: 'newStatus',
                name: 'newStatus',
                label: 'Status',
                type: 'select',
                required: true,
                options: [
                    { label: 'Todo', value: 'todo' },
                    { label: 'In Progress', value: 'in_progress' },
                    { label: 'Done', value: 'done' },
                    { label: 'Cancelled', value: 'cancelled' },
                ],
            },
        ],
    },
    when_date_arrives: {
        type: 'when_date_arrives',
        label: 'When date arrives',
        description: 'Triggers on a specific date/time',
        icon: 'Calendar',
        category: 'schedule',
        configurable: true,
        fields: [
            {
                id: 'source',
                name: 'source',
                label: 'Item type',
                type: 'select',
                required: true,
                options: [
                    { label: 'Calendar event', value: 'calendar_events' },
                    { label: 'Task/Step', value: 'steps' },
                    { label: 'Mission', value: 'missions' },
                ],
            },
            {
                id: 'field',
                name: 'field',
                label: 'Date field',
                type: 'text',
                required: true,
                placeholder: 'e.g., due_date, scheduled_at',
            },
            {
                id: 'triggerBefore',
                name: 'triggerBefore',
                label: 'Trigger before (minutes)',
                type: 'number',
                required: false,
                placeholder: '0 for exact time, 60 for 1 hour before',
            },
        ],
    },
    when_form_submitted: {
        type: 'when_form_submitted',
        label: 'When form is submitted',
        description: 'Triggers when a form is submitted',
        icon: 'Edit',
        category: 'form',
        configurable: true,
        fields: [
            {
                id: 'formId',
                name: 'formId',
                label: 'Form ID',
                type: 'text',
                required: true,
            },
        ],
    },
    when_column_changes: {
        type: 'when_column_changes',
        label: 'When column changes',
        description: 'Triggers when item moves between columns',
        icon: 'Settings',
        category: 'data',
        configurable: true,
        fields: [
            {
                id: 'boardId',
                name: 'boardId',
                label: 'Board ID',
                type: 'text',
                required: true,
            },
            {
                id: 'columnId',
                name: 'columnId',
                label: 'Column ID',
                type: 'text',
                required: false,
                helpText: 'Leave empty to trigger on any column change',
            },
        ],
    },
    when_task_completed: {
        type: 'when_task_completed',
        label: 'When task completed',
        description: 'Triggers when a task is marked complete',
        icon: 'CheckCircle',
        category: 'data',
        configurable: true,
        fields: [
            {
                id: 'source',
                name: 'source',
                label: 'Task type',
                type: 'select',
                required: true,
                options: [
                    { label: 'Mission', value: 'missions' },
                    { label: 'Project task', value: 'project_tasks' },
                    { label: 'Step/Task', value: 'steps' },
                ],
            },
        ],
    },
    manual_trigger: {
        type: 'manual_trigger',
        label: 'Manual trigger',
        description: 'Manually run this workflow anytime',
        icon: 'Zap',
        category: 'manual',
        configurable: false,
    },
};
// ============================================================================
// ACTION DEFINITIONS
// ============================================================================
export const ACTION_DEFINITIONS = {
    create_item: {
        type: 'create_item',
        label: 'Create item',
        description: 'Create a new item in a table',
        icon: 'Plus',
        category: 'item',
        fields: [
            {
                id: 'targetTable',
                name: 'targetTable',
                label: 'Create in',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Task/Step', value: 'steps' },
                    { label: 'Mission', value: 'missions' },
                    { label: 'Length', value: 'lengths' },
                ],
            },
            {
                id: 'title',
                name: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                placeholder: 'Use {{variable}} to reference trigger data',
            },
            {
                id: 'description',
                name: 'description',
                label: 'Description',
                type: 'textarea',
                required: false,
            },
        ],
    },
    update_item: {
        type: 'update_item',
        label: 'Update item',
        description: 'Update an existing item',
        icon: 'Edit',
        category: 'item',
        fields: [
            {
                id: 'targetTable',
                name: 'targetTable',
                label: 'Update in',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Task/Step', value: 'steps' },
                    { label: 'Mission', value: 'missions' },
                ],
            },
            {
                id: 'targetId',
                name: 'targetId',
                label: 'Item ID',
                type: 'text',
                required: false,
                placeholder: 'Use {{id}} from trigger data',
            },
            {
                id: 'status',
                name: 'status',
                label: 'Status',
                type: 'select',
                required: false,
                options: [
                    { label: 'Todo', value: 'todo' },
                    { label: 'In Progress', value: 'in_progress' },
                    { label: 'Done', value: 'done' },
                    { label: 'Cancelled', value: 'cancelled' },
                ],
            },
        ],
    },
    create_task: {
        type: 'create_task',
        label: 'Create task',
        description: 'Create a new task/mission',
        icon: 'Plus',
        category: 'task',
        fields: [
            {
                id: 'title',
                name: 'title',
                label: 'Task title',
                type: 'text',
                required: true,
            },
            {
                id: 'description',
                name: 'description',
                label: 'Description',
                type: 'textarea',
                required: false,
            },
            {
                id: 'dueDate',
                name: 'dueDate',
                label: 'Due date',
                type: 'date',
                required: false,
            },
            {
                id: 'priority',
                name: 'priority',
                label: 'Priority',
                type: 'select',
                required: false,
                options: [
                    { label: 'Low', value: 'low' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'High', value: 'high' },
                ],
            },
        ],
    },
    send_notification: {
        type: 'send_notification',
        label: 'Send notification',
        description: 'Send a notification to users',
        icon: 'Bell',
        category: 'notification',
        fields: [
            {
                id: 'type',
                name: 'type',
                label: 'Notification type',
                type: 'select',
                required: true,
                options: [
                    { label: 'In-app notification', value: 'in_app' },
                    { label: 'Email', value: 'email' },
                    { label: 'Webhook', value: 'webhook' },
                ],
            },
            {
                id: 'message',
                name: 'message',
                label: 'Message',
                type: 'textarea',
                required: true,
            },
        ],
    },
    add_comment: {
        type: 'add_comment',
        label: 'Add comment',
        description: 'Add a comment to an item',
        icon: 'MessageSquare',
        category: 'item',
        fields: [
            {
                id: 'targetTable',
                name: 'targetTable',
                label: 'Add comment to',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Mission', value: 'missions' },
                ],
            },
            {
                id: 'text',
                name: 'text',
                label: 'Comment text',
                type: 'textarea',
                required: true,
            },
        ],
    },
    trigger_webhook: {
        type: 'trigger_webhook',
        label: 'Trigger webhook',
        description: 'Send data to an external webhook',
        icon: 'Webhook',
        category: 'integration',
        fields: [
            {
                id: 'url',
                name: 'url',
                label: 'Webhook URL',
                type: 'text',
                required: true,
            },
            {
                id: 'method',
                name: 'method',
                label: 'HTTP method',
                type: 'select',
                required: true,
                options: [
                    { label: 'POST', value: 'POST' },
                    { label: 'PUT', value: 'PUT' },
                    { label: 'GET', value: 'GET' },
                    { label: 'DELETE', value: 'DELETE' },
                ],
            },
        ],
    },
    duplicate_item: {
        type: 'duplicate_item',
        label: 'Duplicate item',
        description: 'Create a copy of an item',
        icon: 'Copy',
        category: 'item',
        fields: [
            {
                id: 'sourceTable',
                name: 'sourceTable',
                label: 'Duplicate from',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Task/Step', value: 'steps' },
                    { label: 'Mission', value: 'missions' },
                ],
            },
        ],
    },
    archive_item: {
        type: 'archive_item',
        label: 'Archive item',
        description: 'Archive an item',
        icon: 'Archive',
        category: 'item',
        fields: [
            {
                id: 'targetTable',
                name: 'targetTable',
                label: 'Archive from',
                type: 'select',
                required: true,
                options: [
                    { label: 'Board item', value: 'board_items' },
                    { label: 'Mission', value: 'missions' },
                ],
            },
        ],
    },
};
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
export function getTriggerDefinition(triggerType) {
    return TRIGGER_DEFINITIONS[triggerType] || null;
}
export function getActionDefinition(actionType) {
    return ACTION_DEFINITIONS[actionType] || null;
}
export function getAllTriggers() {
    return Object.values(TRIGGER_DEFINITIONS);
}
export function getAllActions() {
    return Object.values(ACTION_DEFINITIONS);
}
export function getTriggersByCategory(category) {
    return getAllTriggers().filter((t) => t.category === category);
}
export function getActionsByCategory(category) {
    return getAllActions().filter((a) => a.category === category);
}
